import { useEffect } from "react";
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

const DEFAULT_AMPLIFICATION = 5;

/**
 * Trackpad pinch-to-zoom is reported by the browser as `wheel` events with
 * ctrlKey=true, using much smaller deltaY ticks than a physical mouse-wheel
 * notch — so with react-zoom-pan-pinch's default smooth-wheel step, the same
 * pinch gesture zooms noticeably less than an equivalent wheel scroll.
 *
 * This hook intercepts just those ctrl+wheel events, in the capture phase on
 * `document` (so it always runs before rzpp's own "wheel" listener, which it
 * attaches directly on the wrapper element, regardless of mount/registration
 * order), and multiplies their delta before letting them through. It replaces
 * the original event with an amplified copy dispatched at the same target,
 * tagged so the handler ignores its own re-dispatch instead of amplifying
 * repeatedly. Ordinary wheel scrolling (ctrlKey=false) is left untouched.
 *
 * To disable this behavior entirely, just remove the hook call in
 * DockedVisualizerWrapper.js — no other changes needed.
 *
 * @param transformRef - the same ref passed to <TransformWrapper ref={...}>.
 * @param amplification - multiplier applied to ctrl+wheel deltaY. Defaults to 5.
 */
export default function useLaptopPinchIncrease(
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>,
  amplification: number = DEFAULT_AMPLIFICATION,
): void {
  useEffect(() => {
    const handlePinchWheelCapture = (event: WheelEvent & { __pinchAmplified?: boolean }) => {
      if (event.__pinchAmplified || !event.ctrlKey) return;

      const ref = transformRef.current;
      const wrapperEl = ref && ref.instance && ref.instance.wrapperComponent;
      if (!wrapperEl || !wrapperEl.contains(event.target as Node)) return;

      event.preventDefault();
      event.stopPropagation();

      const amplified: WheelEvent & { __pinchAmplified?: boolean } = new WheelEvent("wheel", {
        bubbles: true,
        cancelable: true,
        deltaX: event.deltaX,
        deltaY: event.deltaY * amplification,
        deltaZ: event.deltaZ,
        deltaMode: event.deltaMode,
        clientX: event.clientX,
        clientY: event.clientY,
        ctrlKey: true,
      });
      amplified.__pinchAmplified = true;
      event.target.dispatchEvent(amplified);
    };

    document.addEventListener("wheel", handlePinchWheelCapture, {
      capture: true,
      passive: false,
    });
    return () => {
      document.removeEventListener("wheel", handlePinchWheelCapture, { capture: true } as EventListenerOptions);
    };
  }, [transformRef, amplification]);
}
